export const MAX_MARGIN_INPUT_LENGTH = 128
export const MONEY_DECIMAL_PLACES = 2
export const PERCENT_DECIMAL_PLACES = 4

export type MarginFieldKey = 'cost' | 'sale' | 'profit' | 'margin' | 'markup'
export type MarginPairKey =
  | 'cost-sale'
  | 'cost-profit'
  | 'cost-margin'
  | 'cost-markup'
  | 'sale-profit'
  | 'sale-margin'
  | 'sale-markup'
  | 'profit-margin'
  | 'profit-markup'
  | 'margin-markup'

export type MarginCalculationError =
  | 'same_field'
  | 'empty_input'
  | 'invalid_number'
  | 'number_too_long'
  | 'singular_percentage'
  | 'undefined_selected_percentage'
  | 'insufficient_scale'

export interface MarginInputs {
  cost: string
  sale: string
  profit: string
  margin: string
  markup: string
}

interface Rational {
  numerator: bigint
  denominator: bigint
}

export interface FormattedMarginValue {
  text: string
  unit: 'ден. ед.' | '%'
  rounded: boolean
}

export interface MarginCalculationResult {
  values: Record<MarginFieldKey, FormattedMarginValue | null>
  formula: string
  pair: MarginPairKey
}

export type MarginCalculation =
  | { status: 'success'; result: MarginCalculationResult }
  | { status: 'error'; error: MarginCalculationError; invalidFields: MarginFieldKey[] }

const FIELD_ORDER: MarginFieldKey[] = ['cost', 'sale', 'profit', 'margin', 'markup']
const DECIMAL_PATTERN = /^[+-]?(?:\d+|\d+[.,]\d+|[.,]\d+)$/
const HUNDRED = rational(100n)

function absolute(value: bigint): bigint {
  return value < 0n ? -value : value
}

function greatestCommonDivisor(left: bigint, right: bigint): bigint {
  let a = absolute(left)
  let b = absolute(right)
  while (b !== 0n) {
    const remainder = a % b
    a = b
    b = remainder
  }
  return a || 1n
}

function rational(numerator: bigint, denominator = 1n): Rational {
  if (denominator === 0n) throw new RangeError('Rational denominator cannot be zero')
  const sign = denominator < 0n ? -1n : 1n
  const divisor = greatestCommonDivisor(numerator, denominator)
  return {
    numerator: (numerator / divisor) * sign,
    denominator: absolute(denominator / divisor),
  }
}

function add(left: Rational, right: Rational): Rational {
  return rational(
    left.numerator * right.denominator + right.numerator * left.denominator,
    left.denominator * right.denominator,
  )
}

function subtract(left: Rational, right: Rational): Rational {
  return rational(
    left.numerator * right.denominator - right.numerator * left.denominator,
    left.denominator * right.denominator,
  )
}

function multiply(left: Rational, right: Rational): Rational {
  return rational(left.numerator * right.numerator, left.denominator * right.denominator)
}

function divide(left: Rational, right: Rational): Rational {
  return rational(left.numerator * right.denominator, left.denominator * right.numerator)
}

function isZero(value: Rational): boolean {
  return value.numerator === 0n
}

function parseDecimal(input: string): Rational | null {
  const normalized = input.trim().replace(',', '.')
  if (!DECIMAL_PATTERN.test(normalized)) return null
  const negative = normalized.startsWith('-')
  const unsigned = normalized.replace(/^[+-]/, '')
  const [integer = '0', fraction = ''] = unsigned.split('.')
  const digits = `${integer || '0'}${fraction}`.replace(/^0+(?=\d)/, '') || '0'
  return rational(
    BigInt(`${negative ? '-' : ''}${digits}`),
    10n ** BigInt(fraction.length),
  )
}

export function marginPairKey(first: MarginFieldKey, second: MarginFieldKey): MarginPairKey | null {
  if (first === second) return null
  const ordered = [first, second].sort(
    (left, right) => FIELD_ORDER.indexOf(left) - FIELD_ORDER.indexOf(right),
  )
  return `${ordered[0]}-${ordered[1]}` as MarginPairKey
}

export function calculateMarginMarkup(
  first: MarginFieldKey,
  second: MarginFieldKey,
  inputs: MarginInputs,
): MarginCalculation {
  const pair = marginPairKey(first, second)
  if (!pair) return { status: 'error', error: 'same_field', invalidFields: [first] }

  const parsed: Partial<Record<MarginFieldKey, Rational>> = {}
  for (const field of [first, second]) {
    const input = inputs[field]
    if (input.trim() === '') {
      return { status: 'error', error: 'empty_input', invalidFields: [field] }
    }
    if (input.length > MAX_MARGIN_INPUT_LENGTH) {
      return { status: 'error', error: 'number_too_long', invalidFields: [field] }
    }
    const value = parseDecimal(input)
    if (!value) return { status: 'error', error: 'invalid_number', invalidFields: [field] }
    parsed[field] = value
  }

  if (pair === 'margin-markup') {
    return { status: 'error', error: 'insufficient_scale', invalidFields: ['margin', 'markup'] }
  }

  let cost: Rational
  let sale: Rational
  let formula: string

  if (pair === 'cost-sale') {
    cost = parsed.cost!
    sale = parsed.sale!
    formula = 'Прибыль = цена продажи − себестоимость'
  } else if (pair === 'cost-profit') {
    cost = parsed.cost!
    sale = add(cost, parsed.profit!)
    formula = 'Цена продажи = себестоимость + прибыль'
  } else if (pair === 'cost-margin') {
    const denominator = subtract(HUNDRED, parsed.margin!)
    if (isZero(denominator)) {
      return { status: 'error', error: 'singular_percentage', invalidFields: ['margin'] }
    }
    cost = parsed.cost!
    sale = divide(multiply(HUNDRED, cost), denominator)
    formula = 'Цена продажи = 100 × себестоимость ÷ (100 − маржа)'
  } else if (pair === 'cost-markup') {
    cost = parsed.cost!
    sale = add(cost, divide(multiply(cost, parsed.markup!), HUNDRED))
    formula = 'Цена продажи = себестоимость × (1 + наценка ÷ 100)'
  } else if (pair === 'sale-profit') {
    sale = parsed.sale!
    cost = subtract(sale, parsed.profit!)
    formula = 'Себестоимость = цена продажи − прибыль'
  } else if (pair === 'sale-margin') {
    sale = parsed.sale!
    cost = subtract(sale, divide(multiply(sale, parsed.margin!), HUNDRED))
    formula = 'Себестоимость = цена продажи × (1 − маржа ÷ 100)'
  } else if (pair === 'sale-markup') {
    const denominator = add(HUNDRED, parsed.markup!)
    if (isZero(denominator)) {
      return { status: 'error', error: 'singular_percentage', invalidFields: ['markup'] }
    }
    sale = parsed.sale!
    cost = divide(multiply(HUNDRED, sale), denominator)
    formula = 'Себестоимость = 100 × цена продажи ÷ (100 + наценка)'
  } else if (pair === 'profit-margin') {
    if (isZero(parsed.margin!)) {
      return { status: 'error', error: 'singular_percentage', invalidFields: ['margin'] }
    }
    const profit = parsed.profit!
    sale = divide(multiply(HUNDRED, profit), parsed.margin!)
    cost = subtract(sale, profit)
    formula = 'Цена продажи = 100 × прибыль ÷ маржа'
  } else {
    if (isZero(parsed.markup!)) {
      return { status: 'error', error: 'singular_percentage', invalidFields: ['markup'] }
    }
    const profit = parsed.profit!
    cost = divide(multiply(HUNDRED, profit), parsed.markup!)
    sale = add(cost, profit)
    formula = 'Себестоимость = 100 × прибыль ÷ наценка'
  }

  if (parsed.margin !== undefined && isZero(sale)) {
    return {
      status: 'error',
      error: 'undefined_selected_percentage',
      invalidFields: ['margin'],
    }
  }
  if (parsed.markup !== undefined && isZero(cost)) {
    return {
      status: 'error',
      error: 'undefined_selected_percentage',
      invalidFields: ['markup'],
    }
  }

  const profit = subtract(sale, cost)
  const margin = isZero(sale) ? null : divide(multiply(profit, HUNDRED), sale)
  const markup = isZero(cost) ? null : divide(multiply(profit, HUNDRED), cost)
  return {
    status: 'success',
    result: {
      pair,
      formula,
      values: {
        cost: formatValue(cost, MONEY_DECIMAL_PLACES, 'ден. ед.'),
        sale: formatValue(sale, MONEY_DECIMAL_PLACES, 'ден. ед.'),
        profit: formatValue(profit, MONEY_DECIMAL_PLACES, 'ден. ед.'),
        margin: margin ? formatValue(margin, PERCENT_DECIMAL_PLACES, '%') : null,
        markup: markup ? formatValue(markup, PERCENT_DECIMAL_PLACES, '%') : null,
      },
    },
  }
}

function formatValue(
  value: Rational,
  decimalPlaces: number,
  unit: FormattedMarginValue['unit'],
): FormattedMarginValue {
  const factor = 10n ** BigInt(decimalPlaces)
  const negative = value.numerator < 0n
  const scaledNumerator = absolute(value.numerator) * factor
  let roundedInteger = scaledNumerator / value.denominator
  const remainder = scaledNumerator % value.denominator
  if (remainder * 2n >= value.denominator) roundedInteger += 1n
  const integer = roundedInteger / factor
  const fraction = (roundedInteger % factor)
    .toString()
    .padStart(decimalPlaces, '0')
    .replace(/0+$/, '')
  const magnitude = fraction ? `${integer}.${fraction}` : integer.toString()
  return {
    text: `${negative && roundedInteger !== 0n ? '-' : ''}${magnitude}`,
    unit,
    rounded: remainder !== 0n,
  }
}
